import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Clock, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { cn } from '@/lib/utils';
import { TerminalAuthError } from '@/auth/authErrors';
import { Button } from '@/components/Button';
import { LogoLoader } from '@/components/LogoLoader/LogoLoader';
import { OtpInput } from '@/components/OtpInput';
import styles from './TwoFactorVerify.module.css';

const DEFAULT_CODE_LENGTH = 6;
// Matches OTP_TTL in backend/src/modules/otp/otp.constants.ts (10 min). The
// API doesn't return the actual expiry timestamp, so this is a best-effort
// countdown — wrong only if that constant changes.
const DEFAULT_CODE_EXPIRY_SECONDS = 10 * 60;
// Matches OTP_RESEND_COOLDOWN_SECONDS in
// window always fails server-side, so disable the button client-side too
// instead of letting the user fire a request that's guaranteed to be
// rejected. Only applies *after* a resend actually happens: the initial
// OTP send (issueChallenge(), on login) never starts this cooldown, so the
// button starts enabled — see the resendCooldown useState below.
const RESEND_COOLDOWN_SECONDS = 60;

function formatCountdown(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export interface TwoFactorVerifyProps {
  email: string;
  onVerify: (code: string) => Promise<void> | void;
  onResend: () => Promise<void> | void;
  /** Omit to hide the back link — e.g. when the host renders its own close
   * affordance (a dialog header close button) instead. */
  onBack?: () => void;
  codeLength?: number;
  codeExpirySeconds?: number;
  /** Set when rendering inside a dialog/modal that already has its own
   * elevation (e.g. admin's `openDialog` usage) — omits the card's own
   * drop shadow so the two don't double up. */
  flat?: boolean;
  /** Set when the host already shows its own "Verify your identity" title
   * (e.g. a dialog header) — hides the card's own `<h2>` so the two don't
   * repeat the same text. The icon and the "code sent to <email>" subtext
   * stay, since that's information the host's title doesn't carry. */
  hideHeading?: boolean;
  /** Called once the current challenge can never succeed again (see
   * TerminalAuthError) — e.g. to close a dialog or clear pending-auth state
   * so the user lands back on the login form. Falls back to `onBack` when
   * omitted, so hosts that already pass `onBack` don't need to pass both. */
  onTerminalError?: () => void;
}

export function TwoFactorVerify({
  email,
  onVerify,
  onResend,
  onBack,
  codeLength = DEFAULT_CODE_LENGTH,
  codeExpirySeconds = DEFAULT_CODE_EXPIRY_SECONDS,
  flat = false,
  hideHeading = false,
  onTerminalError,
}: TwoFactorVerifyProps) {
  const { t } = useTranslation(TranslationNS.TwoFactor);
  const [code, setCode] = useState('');
  const [expiresIn, setExpiresIn] = useState(codeExpirySeconds);
  const [verifying, setVerifying] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Once true, the challenge is dead server-side — Verify/Resend would only
  // ever fail again, so both get disabled and the only way forward is back.
  const [terminal, setTerminal] = useState(false);
  const handleReturn = onTerminalError ?? onBack;
  // One interval per mount, ticking via the functional updater — no need to
  // tear down and recreate it every second just because `expiresIn` changed
  // (it's read fresh from `s` each tick, and clamped at 0). `terminal` only
  // flips once, so depending on it here doesn't reintroduce that churn — it
  // just stops the countdown for good once the challenge is dead, instead
  // of counting down a code that can never be verified anyway.
  useEffect(() => {
    if (terminal) return;
    const timer = setInterval(
      () => setExpiresIn((s) => (s <= 0 ? 0 : s - 1)),
      1000
    );
    return () => clearInterval(timer);
  }, [terminal]);

  useEffect(() => {
    if (terminal) return;
    const timer = setInterval(
      () => setResendCooldown((s) => (s <= 0 ? 0 : s - 1)),
      1000
    );
    return () => clearInterval(timer);
  }, [terminal]);

  const isComplete = code.length === codeLength;
  const expiryPercent = Math.max(0, (expiresIn / codeExpirySeconds) * 100);
  // Once this hits 0 the code is certainly expired — don't let Verify spend
  // a round trip finding that out server-side; point the user at Resend.
  const expired = expiresIn <= 0;
  // Verify and Resend both mutate the same pending challenge server-side, so
  // letting one fire while the other is in flight (or on cooldown) races a
  // code check against a request that's about to invalidate it.
  const busy = verifying || resendLoading;

  function getErrorMessage(err: unknown): string {
    return err instanceof Error && err.message
      ? err.message
      : t('invalid_code');
  }

  async function handleVerify() {
    if (!isComplete || busy || terminal || expired) return;
    setError(null);
    setVerifying(true);
    try {
      await onVerify(code);
    } catch (err) {
      // A wrong code with attempts left is retryable — the session is still
      // pending, so clear the code and let the user retype or resend. A
      // TerminalAuthError means the challenge is dead (max attempts, or
      // expired) and neither will ever succeed again — stop letting the
      // user retry so they're not stuck relying on a manual reload.
      setError(getErrorMessage(err));
      setCode('');
      if (err instanceof TerminalAuthError) {
        setTerminal(true);
      }
    } finally {
      setVerifying(false);
    }
  }

  async function handleResend() {
    if (busy || resendCooldown > 0 || terminal) return;
    setError(null);
    setResendLoading(true);
    try {
      await onResend();
      setCode('');
      setExpiresIn(codeExpirySeconds);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(getErrorMessage(err));
      if (err instanceof TerminalAuthError) {
        setTerminal(true);
      }
    } finally {
      setResendLoading(false);
    }
  }

  return (
    <div className={cn(styles.card, flat && styles.cardFlat)}>
      <div className={styles.topSection}>
        <div className={styles.iconWrap}>
          <Mail size={26} className={styles.icon} />
        </div>
        {!hideHeading && <h2 className={styles.heading}>{t('heading')}</h2>}
        <p className={styles.subtext}>
          {t('subtext')} <span className={styles.emailHighlight}>{email}</span>
        </p>
      </div>

      <div className={styles.bottomSection}>
        <p className={styles.codeLabel}>{t('code_label')}</p>

        <OtpInput
          value={code}
          onChange={setCode}
          error={!!error}
          disabled={busy || terminal}
          length={codeLength}
          groupLabel={t('otp_group_label')}
          digitLabel={(index, total) => t('otp_digit_label', { index, total })}
          onEnter={handleVerify}
        />

        {error && (
          <p role="alert" className={styles.serverError}>
            {error}
          </p>
        )}

        <div className={styles.expiryRow}>
          <span className={styles.expiryLabel}>
            <Clock size={14} />
            {t('code_expires_in')}
          </span>
          <span className={styles.expiryTime}>
            {formatCountdown(expiresIn)}
          </span>
        </div>
        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{ width: `${expiryPercent}%` }}
          />
        </div>

        <p className={styles.resendRow}>
          {t('resend_prompt')}{' '}
          <button
            type="button"
            onClick={handleResend}
            disabled={busy || resendCooldown > 0 || terminal}
            className={styles.resendBtn}
          >
            {resendLoading
              ? t('resending')
              : resendCooldown > 0
                ? t('resend_cooldown', { count: resendCooldown })
                : t('resend')}
          </button>
        </p>

        <Button
          variant="primary"
          IconSuffix={verifying ? undefined : ArrowRight}
          disabled={busy || !isComplete || terminal || expired}
          className={styles.submitBtn}
          onClick={handleVerify}
        >
          {verifying ? <LogoLoader size="sm" /> : t('submit')}
        </Button>

        {(terminal ? handleReturn : onBack) && (
          <div className={styles.backRow}>
            <button
              type="button"
              onClick={terminal ? handleReturn : onBack}
              className={styles.backLink}
            >
              <ArrowLeft size={14} />
              {t('back_link')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
